import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Boxes,
  Building2,
  FileSpreadsheet,
  FileText,
  Globe,
  Images,
  LayoutGrid,
  LogOut,
  Megaphone,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  Store,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "../lib/api";
import i18n, { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from "../lib/i18n";
import { useCompanySettings } from "../lib/useCompanySettings";
import { useSession } from "../lib/useSession";
import { cn } from "../lib/utils";
import { VoiceAssistant } from "./VoiceAssistant";

const NAV = [
  { href: "/", key: "navigation:apps", Icon: LayoutGrid },
  { href: "/alerts", key: "navigation:alerts", Icon: AlertTriangle },
  { href: "/inventory", key: "navigation:inventory", Icon: Boxes },
  { href: "/import", key: "navigation:import", Icon: FileSpreadsheet },
  { href: "/marketplaces", key: "navigation:marketplaces", Icon: Store },
  { href: "/sales", key: "navigation:sales", Icon: Receipt },
  { href: "/advertising", key: "navigation:advertising", Icon: Megaphone },
  { href: "/designs", key: "navigation:designs", Icon: Images },
  { href: "/manual", key: "navigation:manual", Icon: ScrollText },
  { href: "/changelog", key: "navigation:changelog", Icon: Sparkles },
] as const;

export function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { user } = useSession();
  const { settings } = useCompanySettings();
  // Hiding the link is a courtesy; the server enforces "*" independently.
  const isAdmin = user?.scopes?.includes("*") ?? false;
  // Same "marketplaces" scope server/routes/assistant.ts requires — hiding
  // the button for someone who'd just get a 403 asking it anything.
  const canUseAssistant = isAdmin || (user?.scopes?.includes("marketplaces") ?? false);
  // Same gate server/routes/invoices.ts enforces — hiding the button for
  // someone who'd just get a 403 generating one.
  const canGenerateInvoices = isAdmin || (user?.scopes?.includes("invoices") ?? false);

  // Urgent-alerts tab count (2026-10-12): visible from every page, so the tab
  // itself shows when something needs a shipment decision today. Polls every
  // 5 min; counts only critical/high.
  const canSeeInventory = isAdmin || (user?.scopes?.includes("inventory") ?? false);
  const { data: restockAlerts } = useQuery({
    queryKey: ["restock-alert-count"],
    queryFn: () =>
      api<{ severity: string; acknowledgedAt: string | null }[]>(
        "/api/v1/inventory/restock-alerts",
      ),
    enabled: canSeeInventory,
    refetchInterval: 300_000,
    staleTime: 60_000,
  });
  const restockCount =
    restockAlerts?.filter((a) => a.severity !== "watch" && !a.acknowledgedAt).length ?? 0;

  return (
    <div className="min-h-dvh flex flex-col">
      <header className="border-b bg-card">
        <div className="mx-auto max-w-[130rem] px-4 py-3 flex flex-wrap items-center gap-3">
          <Link href="/" className="tap flex items-center gap-2 font-semibold tracking-tight">
            {settings?.logoDataUrl ? (
              <img
                src={settings.logoDataUrl}
                alt=""
                className="h-8 w-8 rounded-lg object-contain"
              />
            ) : (
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-bold">
                N
              </span>
            )}
            <span className="text-lg">{settings?.companyName || t("common:appName")}</span>
          </Link>

          {/* Scrolls horizontally rather than clipping or wrapping — 7 module
              tabs no longer reliably fit in one row at a narrow phone width,
              and the alternative (wrap) would push the icon row onto its own
              line under the logo on every page. Access/Settings moved to the
              sprocket menu on the right, not in this row at all. */}
          <nav
            className="flex min-w-0 items-center gap-1 overflow-x-auto sm:ml-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label={t("navigation:primary")}
          >
            {NAV.filter(
              ({ href }) => (href !== "/alerts" && href !== "/import") || canSeeInventory,
            ).map(({ href, key, Icon }) => {
              const active = href === "/" ? location === "/" : location.startsWith(href);
              return (
                <Button
                  key={href}
                  asChild
                  variant={active ? "secondary" : "ghost"}
                  size="sm"
                  className="shrink-0 px-3"
                >
                  {/* aria-label is always present: the text label is hidden below
                      `sm` and the icon is aria-hidden, so without it this control
                      has NO accessible name on a phone. */}
                  <Link href={href} aria-label={t(key)}>
                    <Icon className="h-4 w-4" aria-hidden />
                    <span className={cn("hidden sm:inline")}>{t(key)}</span>
                    {href === "/alerts" && restockCount > 0 && (
                      <span className="ml-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
                        {restockCount}
                      </span>
                    )}
                  </Link>
                </Button>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {canGenerateInvoices && (
              <Button asChild variant="outline" size="sm" className="shrink-0 px-3">
                <Link href="/invoices/new" aria-label={t("navigation:generateInvoice")}>
                  <FileText className="h-4 w-4" aria-hidden />
                  <span className="hidden sm:inline">{t("navigation:generateInvoice")}</span>
                </Link>
              </Button>
            )}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-3"
                    aria-label={t("settings:menu")}
                  >
                    <Settings className="h-4 w-4" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link href="/admin/settings">
                      <Building2 className="h-4 w-4" aria-hidden />
                      {t("settings:companySettings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/admin/access">
                      <ShieldCheck className="h-4 w-4" aria-hidden />
                      {t("navigation:access")}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Globe className="h-4 w-4 text-muted-foreground" aria-hidden />
            <Select
              value={i18n.resolvedLanguage}
              onValueChange={(value) => void i18n.changeLanguage(value)}
            >
              <SelectTrigger aria-label={t("common:language")} className="w-[9.5rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_LANGUAGES.map((lng) => (
                  <SelectItem key={lng} value={lng}>
                    {LANGUAGE_LABELS[lng]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {user ? (
              <form method="post" action="/auth/logout">
                <Button
                  type="submit"
                  variant="ghost"
                  size="sm"
                  className="px-3"
                  aria-label={t("auth:signOut")}
                >
                  <LogOut className="h-4 w-4" aria-hidden />
                  <span className="hidden sm:inline">{t("auth:signOut")}</span>
                </Button>
              </form>
            ) : (
              <Button asChild size="sm">
                <a href="/auth/login">{t("auth:signIn")}</a>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Widened 2026-09-23 (Isaac: "you need to widen the main box") --
          Inventory's table (12 columns + a 5-button actions column) was
          overflowing this box's own inner scroll well before it ran out of
          REAL screen space on a normal monitor; header/footer widened to
          match so the page's edges stay aligned at every width. */}
      <main className="mx-auto w-full max-w-[130rem] flex-1 px-4 py-6 sm:py-8">{children}</main>

      <footer className="border-t bg-card">
        <div className="mx-auto max-w-[130rem] px-4 py-4 text-sm text-muted-foreground">
          {t("common:footer")}
        </div>
      </footer>

      {canUseAssistant && <VoiceAssistant />}
    </div>
  );
}
